import { useCallback } from 'react';
import { useSelectedVersion } from '@client/store/ui';
import { useProposalsResultRecords } from '@features/proposals/use-proposals.hooks';
import { useProjectBuildingGFA } from '@client/store/project';
import { useEffectiveSbefGroups } from '@features/sbef/use-effective-sbef-groups.hook';
import { useSelectedLifecycles } from '@features/lifecycles/LifecyclesSelect';
import { useConservativeGenericCarbon } from '@features/results/conservative-generic-carbon.hook';
import { useProductsLookup } from '@client/store/product/product.hook';
import { getResolvedBuildingVersion } from '@shared/helpers/actions/resolved-version.helpers';
import type { IProposal } from '@shared/models/proposals.model';
import type { ResultsRecord } from '@shared/models/unit.model';
import { filterProductGroupChartResultsRecord } from '../ReportProductGroups/report-product-groups-results.helpers';
import {
  buildTopProducts,
  type TopProductsData,
} from './report-top-products-data.helpers';

export const useGetReportTopProductsByProposal = (): ((
  proposalId: IProposal['id'],
) => TopProductsData | undefined) => {
  const version = useSelectedVersion();
  const rawProposalRecords = useProposalsResultRecords(false);
  const gfa = useProjectBuildingGFA();
  const effectiveSbefGroups = useEffectiveSbefGroups();
  const selectedLifecycles = useSelectedLifecycles();
  const conservative = useConservativeGenericCarbon();
  const productLookup = useProductsLookup();

  const filterResultsRecord = useCallback(
    (record: ResultsRecord) =>
      filterProductGroupChartResultsRecord(record, {
        lifecycles: selectedLifecycles,
        conservative,
      }),
    [selectedLifecycles, conservative],
  );

  return useCallback(
    (proposalId) => {
      if (!version) return undefined;
      const isBaselineScenario = proposalId === version.id;
      const scenarioVersion = isBaselineScenario
        ? version
        : getResolvedBuildingVersion(version, proposalId);

      return buildTopProducts({
        baselineVersion: version,
        scenarioVersion,
        baselineRecord: filterResultsRecord(
          rawProposalRecords[version.id] ?? {},
        ),
        scenarioRecord: filterResultsRecord(
          rawProposalRecords[proposalId] ?? {},
        ),
        selectedSbefGroups: effectiveSbefGroups,
        gfa,
        productLookup,
      });
    },
    [
      version,
      rawProposalRecords,
      filterResultsRecord,
      effectiveSbefGroups,
      gfa,
      productLookup,
    ],
  );
};
